/**
 * AccessRestrictionsAdmin.tsx — Admin > Access Restrictions.
 *
 * A settings page, not a list: the corp page lists accounts and opens this
 * same form per account, so here the form is the page. The Admin list
 * standards apply only to its Exceptions list, which copies the corp dialog's
 * exception table: Type, User / Address (a green / red mark for an active /
 * inactive user, and their username), Description; active users first, then
 * IP addresses, then inactive users (Type, User / Address and Description
 * re-sort it from their titles); a search box once there are exceptions;
 * the red Delete icon (asks first); and the pagination footer. Exceptions are
 * added with the inline form above the list and cannot be edited, so rows do
 * not open anything and there is no header Add button.
 *
 * On a Locked page (useAdminPageLevel) the tiers sit in an AdminLockedFieldset
 * (shown, not changeable) with no Save, and the inline add form, the Remove
 * icons and the Actions column are gone; the exception search still works.
 */
import { useEffect, useMemo, useState } from 'react';
import { CircleCheck, CircleX, ShieldCheck, Plus, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListTable,
  AdminListToolbar,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import { toast } from '@/store/toastStore';
import {
  getAccessEnabled,
  updateAccessEnabled,
  listAccessRestrictions,
  createAccessRestriction,
  deleteAccessRestriction,
  listUsers,
  type AccessRestrictionRow,
  type AdminUser,
} from '@/services/admin.service';

// Port of legacy admin_ip_addresses.php. Two shared tables:
//   ip_enabled → the per-account "Restrict For" tier flags (one row/account).
//   ip_allowed → exceptions; `address` is a dotted IPv4 OR "UID|<userId>".
// Legacy always writes ip_enabled.enabled = 1 on save (hidden field); the
// effective on/off is governed purely by the three tier checkboxes.

type ExceptionType = 'user' | 'address';

const TYPE_OPTIONS = [
  { value: 'user' as ExceptionType, label: 'User' },
  { value: 'address' as ExceptionType, label: 'IP Address' },
];

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

function isValidIpv4(ip: string): boolean {
  const m = IPV4_RE.exec(ip.trim());
  if (!m) return false;
  return m.slice(1, 5).every((octet) => Number(octet) <= 255);
}

interface Tiers {
  phoneCoords: boolean;
  sysUsers: boolean;
  sysAdmins: boolean;
}

/** What an exception row shows: a user (by name) or an IP address. */
interface ResolvedTarget {
  label: string;
  /** The user's username, shown greyed after the name. */
  userName: string | null;
  isUser: boolean;
  /** For a user still on the account: whether they are active. Null for an IP or an unknown user. */
  isActive: boolean | null;
}

// The corp exception table's columns.
const EXCEPTION_COLUMNS: AdminListColumn[] = [
  { key: 'type', header: 'Type', className: 'w-20', sortable: true },
  { key: 'target', header: 'User / Address', sortable: true },
  { key: 'description', header: 'Description', sortable: true },
  { ...ACTIONS_COLUMN, className: 'w-24' },
];

/** A Locked page cannot remove an exception, so no Actions column. */
const LOCKED_EXCEPTION_COLUMNS = EXCEPTION_COLUMNS.filter((c) => c.key !== ACTIONS_COLUMN.key);

interface ExceptionListRow {
  row: AccessRestrictionRow;
  target: ResolvedTarget;
}

/**
 * A title click re-sorts by that column. The list opens in the corp order
 * (exceptionRank), which is no single column's, so no title shows as sorted
 * until one is clicked; ties keep the corp order.
 */
const EXCEPTION_SORT: ListSortCompare<ExceptionListRow> = {
  type: (a, b) => compareText(a.target.isUser ? 'User' : 'IP', b.target.isUser ? 'User' : 'IP'),
  target: (a, b) => compareText(a.target.label, b.target.label),
  description: (a, b) => compareText(a.row.description, b.row.description),
};

/** Corp order: active users first, then IP addresses, then inactive (or unknown) users. */
function exceptionRank(t: ResolvedTarget): number {
  if (t.isUser && t.isActive === true) return 0;
  if (!t.isUser) return 1;
  return 2;
}

/** "UID|<id>" → the user's name (as the corp exception table shows it); anything else is an IP. */
function resolveExceptionTarget(row: AccessRestrictionRow, userMap: Map<number, AdminUser>): ResolvedTarget {
  const addr = row.address ?? '';
  if (addr.startsWith('UID|')) {
    const id = Number(addr.slice(4).trim());
    const u = userMap.get(id);
    if (!u) return { label: `User #${id}`, userName: null, isUser: true, isActive: null };
    let name = `${u.firstName} ${u.lastName}`.trim();
    if (name && u.suffix?.trim()) name = `${name}, ${u.suffix.trim()}`;
    return {
      label: name || u.userName || `User #${id}`,
      userName: name && u.userName ? u.userName : null,
      isUser: true,
      isActive: u.active,
    };
  }
  return { label: addr, userName: null, isUser: false, isActive: null };
}

const SUBTITLE = 'Choose which user levels may sign in only from approved IP addresses, and who is exempt.';
/** The same, without the invitation to choose (a Locked page). */
const LOCKED_SUBTITLE = 'Which user levels may sign in only from approved IP addresses, and who is exempt.';

export function AccessRestrictionsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const subtitle = locked ? LOCKED_SUBTITLE : SUBTITLE;
  const [tiers, setTiers] = useState<Tiers>({
    phoneCoords: false,
    sysUsers: false,
    sysAdmins: false,
  });
  const [restrictions, setRestrictions] = useState<AccessRestrictionRow[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingTiers, setSavingTiers] = useState(false);

  // Add-exception form
  const [excludeType, setExcludeType] = useState<ExceptionType>('user');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [ipValue, setIpValue] = useState('');
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<AccessRestrictionRow | null>(null);
  const [exceptionSearch, setExceptionSearch] = useState('');

  useEffect(() => {
    Promise.all([getAccessEnabled(), listAccessRestrictions(), listUsers()])
      .then(([enabled, rows, allUsers]) => {
        setTiers({
          phoneCoords: Number(enabled.phoneCoords) === 1,
          sysUsers: Number(enabled.sysUsers) === 1,
          sysAdmins: Number(enabled.sysAdmins) === 1,
        });
        setRestrictions(rows);
        setUsers(allUsers);
      })
      .catch(() => toast.error('Failed to load access restrictions.'))
      .finally(() => setLoading(false));
  }, []);

  // userID → user, for resolving "UID|<id>" exception rows to a readable name.
  const userMap = useMemo(() => {
    const m = new Map<number, AdminUser>();
    users.forEach((u) => m.set(u.userId, u));
    return m;
  }, [users]);

  // Active users only, for the picker (parity with legacy `active = 1` filter).
  const userOptions = useMemo(
    () =>
      users
        .filter((u) => u.active)
        .map((u) => ({
          value: String(u.userId),
          label: `${u.firstName} ${u.lastName}`.trim() || u.userName || `User #${u.userId}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  function resolveTarget(row: AccessRestrictionRow): ResolvedTarget {
    return resolveExceptionTarget(row, userMap);
  }

  const exceptionQuery = exceptionSearch.trim().toLowerCase();
  const exceptionRows = useMemo(
    () =>
      restrictions
        .map((row) => ({ row, target: resolveExceptionTarget(row, userMap) }))
        .filter(
          ({ target }) =>
            exceptionQuery === '' ||
            target.label.toLowerCase().includes(exceptionQuery) ||
            (target.userName ?? '').toLowerCase().includes(exceptionQuery),
        )
        .sort(
          (a, b) =>
            exceptionRank(a.target) - exceptionRank(b.target) ||
            a.target.label.localeCompare(b.target.label, undefined, { sensitivity: 'base' }),
        ),
    [restrictions, userMap, exceptionQuery],
  );

  const exceptionSorting = useListSort(exceptionRows, { compare: EXCEPTION_SORT });
  const exceptionPagination = useListPagination(exceptionSorting.sorted, { resetOn: [exceptionQuery] });

  async function handleSaveTiers() {
    setSavingTiers(true);
    try {
      // enabled is force-set to 1 on every save, matching the legacy hidden field.
      const saved = await updateAccessEnabled({
        enabled: 1,
        phoneCoords: tiers.phoneCoords ? 1 : 0,
        sysUsers: tiers.sysUsers ? 1 : 0,
        sysAdmins: tiers.sysAdmins ? 1 : 0,
      });
      setTiers({
        phoneCoords: Number(saved.phoneCoords) === 1,
        sysUsers: Number(saved.sysUsers) === 1,
        sysAdmins: Number(saved.sysAdmins) === 1,
      });
      toast.success('Access restrictions saved.');
    } catch {
      toast.error('Failed to save access restrictions.');
    } finally {
      setSavingTiers(false);
    }
  }

  async function handleAddException() {
    let address: string;
    if (excludeType === 'user') {
      if (!selectedUserId) {
        toast.warning('Select a user to add.');
        return;
      }
      address = `UID|${selectedUserId}`;
    } else {
      if (!isValidIpv4(ipValue)) {
        toast.warning('Enter a valid IPv4 address (e.g. 192.168.1.100).');
        return;
      }
      address = ipValue.trim();
    }

    const desc = description.trim().slice(0, 50);
    setAdding(true);
    try {
      const created = await createAccessRestriction({ address, description: desc });
      setRestrictions((prev) => [...prev, created]);
      setSelectedUserId(null);
      setIpValue('');
      setDescription('');
      toast.success('Exception added.');
    } catch {
      toast.error('Failed to add exception.');
    } finally {
      setAdding(false);
    }
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    const id = pendingDelete.ipAllowedID;
    try {
      await deleteAccessRestriction(id);
      setRestrictions((prev) => prev.filter((r) => r.ipAllowedID !== id));
      toast.success('Exception removed.');
    } catch {
      toast.error('Failed to remove exception.');
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <PageHeader title="Access Restrictions" subtitle={subtitle} />
        <div className="max-w-3xl">
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader title="Access Restrictions" subtitle={subtitle} />

      <div className="max-w-3xl space-y-6">
        {/* ── Restrict For ─────────────────────────────────────────────── */}
        <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Restrict For</h3>
          </div>
          <p className="text-sm text-slate-500">
            Selected user levels can only sign in from a whitelisted IP address (or if listed as a
            user exception below). Account Admins and Developer Admins are never restricted.
          </p>

          <AdminLockedFieldset>
            <div className="space-y-3 pt-1">
              <Checkbox
                label="Phone Coordinators"
                checked={tiers.phoneCoords}
                onChange={(e) =>
                  setTiers((t) => ({ ...t, phoneCoords: (e.target as HTMLInputElement).checked }))
                }
              />
              <Checkbox
                label="System Users"
                checked={tiers.sysUsers}
                onChange={(e) =>
                  setTiers((t) => ({ ...t, sysUsers: (e.target as HTMLInputElement).checked }))
                }
              />
              <Checkbox
                label="System Admins"
                checked={tiers.sysAdmins}
                onChange={(e) =>
                  setTiers((t) => ({ ...t, sysAdmins: (e.target as HTMLInputElement).checked }))
                }
              />
            </div>
          </AdminLockedFieldset>

          {!locked && (
            <div className="flex justify-end pt-2">
              <Button type="button" onClick={handleSaveTiers} loading={savingTiers}>
                Save
              </Button>
            </div>
          )}
        </section>

        {/* ── Exceptions ───────────────────────────────────────────────── */}
        <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Exceptions</h3>
          </div>

          {/* Add exception (not on a Locked page) */}
          {!locked && (
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-3">
                <FormField label="For">
                  <Select<ExceptionType>
                    options={TYPE_OPTIONS}
                    value={excludeType}
                    onChange={(v) => setExcludeType((v as ExceptionType) ?? 'user')}
                  />
                </FormField>
              </div>

              <div className="sm:col-span-4">
                {excludeType === 'user' ? (
                  <FormField label="User">
                    <Select
                      options={userOptions}
                      value={selectedUserId}
                      onChange={(v) => setSelectedUserId(v)}
                      searchable
                      placeholder="Select one…"
                    />
                  </FormField>
                ) : (
                  <FormField label="IP Address">
                    <Input
                      value={ipValue}
                      onChange={(e) => setIpValue(e.target.value)}
                      placeholder="e.g. 192.168.1.100"
                    />
                  </FormField>
                )}
              </div>

              <div className="sm:col-span-3">
                <FormField label="Description">
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={50}
                    placeholder="Optional"
                  />
                </FormField>
              </div>

              <div className="sm:col-span-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddException}
                  loading={adding}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          )}

          {/* Exception list — the corp exception table, in the Admin list style */}
          {restrictions.length > 0 ? (
            <div data-testid="access-exceptions">
              <AdminListToolbar
                search={exceptionSearch}
                onSearchChange={setExceptionSearch}
                searchPlaceholder="Search exceptions…"
                searchLabel="Search exceptions"
              />
              {exceptionRows.length === 0 ? (
                <AdminListEmpty title="No exceptions match your search" hint="Try adjusting your search." />
              ) : (
                <>
                  <AdminListTable
                    columns={locked ? LOCKED_EXCEPTION_COLUMNS : EXCEPTION_COLUMNS}
                    aria-label="Exceptions"
                    sort={exceptionSorting.sort}
                    onSortChange={exceptionSorting.onSortChange}
                  >
                    {exceptionPagination.pageItems.map(({ row, target }) => (
                      <tr key={row.ipAllowedID} data-exception-id={row.ipAllowedID} className={adminRowClass()}>
                        <td className={ADMIN_TD_CLASS}>{target.isUser ? 'User' : 'IP'}</td>
                        <td className={ADMIN_TD_CLASS}>
                          <span className="flex min-w-0 items-center gap-1.5 text-sm text-slate-900">
                            {target.isUser && target.isActive === true && (
                              <CircleCheck className="h-4 w-4 shrink-0 text-green-500" role="img" aria-label="Active user" />
                            )}
                            {target.isUser && target.isActive === false && (
                              <CircleX className="h-4 w-4 shrink-0 text-red-400" role="img" aria-label="Inactive user" />
                            )}
                            <span className={target.isUser ? 'truncate' : 'truncate font-mono'}>{target.label}</span>
                            {target.userName && <span className="truncate text-slate-400">({target.userName})</span>}
                          </span>
                        </td>
                        <td className={cn(ADMIN_TD_CLASS, 'truncate')}>{row.description || '—'}</td>
                        {!locked && (
                          <td className={ADMIN_TD_CLASS}>
                            <AdminRowActions>
                              <AdminRowAction
                                kind="delete"
                                title="Remove"
                                label={`Remove ${target.label}`}
                                onClick={() => setPendingDelete(row)}
                              />
                            </AdminRowActions>
                          </td>
                        )}
                      </tr>
                    ))}
                  </AdminListTable>
                  <PaginationFooter pagination={exceptionPagination} noun={['exception', 'exceptions']} />
                </>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">{locked ? 'No exceptions.' : 'No exceptions added yet.'}</p>
          )}
        </section>
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Remove exception"
        message={
          pendingDelete
            ? `Remove "${resolveTarget(pendingDelete).label}" from the exception list?`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
}
